import React, { useState, useEffect } from 'react'
import {
  Box,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Typography,
  CircularProgress,
  IconButton,
  InputAdornment,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  Visibility,
  VisibilityOff,
  Save as SaveIcon,
  Delete as DeleteIcon
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import SectionTitle from '../../components/SectionTitle'
import * as vncAPI from '../../data/vnc'

const useStyles = makeStyles(theme => ({
  form: {
    padding: theme.spacing(3),
    maxWidth: 600
  },
  field: {
    marginBottom: theme.spacing(2)
  },
  section: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  actions: {
    marginTop: theme.spacing(3),
    display: 'flex',
    justifyContent: 'space-between'
  },
  divider: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  helperText: {
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    marginTop: theme.spacing(0.5)
  }
}))

export const VncConfigPanel = ({ id }) => {
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [showSshPassword, setShowSshPassword] = useState(false)
  const [hasExistingConfig, setHasExistingConfig] = useState(false)
  const [agents, setAgents] = useState([])
  const [loadingAgents, setLoadingAgents] = useState(false)
  
  const [config, setConfig] = useState({
    vncHost: '',
    vncPort: 5900,
    vncPassword: '',
    enabled: true,
    requiresActivation: false,
    activationScript: '',
    fileTransferEnabled: false,
    vncAgentId: null,
    sshCredentials: {
      host: '',
      port: 22,
      username: '',
      password: '',
      connectionType: 'ssh'
    }
  })

  useEffect(() => {
    if (id) {
      fetchConfig()
    }
  }, [id])

  useEffect(() => {
    if (id && config.requiresActivation) {
      fetchAgents()
    }
  }, [id, config.requiresActivation])

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true)
      const data = await vncAPI.getAgentsForModality(id)
      setAgents(data || [])
    } catch (err) {
      console.error('Error fetching agents:', err)
      setAgents([])
    } finally {
      setLoadingAgents(false)
    }
  }

  const fetchConfig = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await vncAPI.getConfig(id)
      if (data && data.vncHost) {
        setHasExistingConfig(true)
        setConfig({
          vncHost: data.vncHost || '',
          vncPort: data.vncPort || 5900,
          vncPassword: '', // Never pre-fill password
          // Ensure boolean values are actual booleans (MySQL returns 0/1)
          enabled: Boolean(data.enabled ?? true),
          requiresActivation: Boolean(data.requiresActivation),
          activationScript: data.activationScript || '',
          fileTransferEnabled: Boolean(data.fileTransferEnabled),
          vncAgentId: data.vncAgentId || null,
          sshCredentials: {
            host: data.sshCredentials?.host || '',
            port: data.sshCredentials?.port || 22,
            username: data.sshCredentials?.username || '',
            password: '', // Never pre-fill password
            connectionType: data.sshCredentials?.connectionType || 'ssh'
          },
          // Track if credentials exist
          hasVncPassword: Boolean(data.hasVncPassword),
          hasSshCredentials: Boolean(data.hasSshCredentials)
        })
      }
    } catch (err) {
      // 404 is expected if no config exists yet
      if (err.status !== 404) {
        console.error('Error fetching VNC config:', err)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field) => (event) => {
    // Handle both Switch (checkbox) and text inputs
    // Switch components have 'checked' property, text inputs have 'value'
    const value = event.target.hasOwnProperty('checked')
      ? event.target.checked
      : event.target.value
    setConfig(prev => ({ ...prev, [field]: value }))
    setSuccess(null)
  }

  const handleSshChange = (field) => (event) => {
    const value = event.target.value
    setConfig(prev => {
      const updates = { [field]: value }
      
      // Auto-update port when connection type changes
      if (field === 'connectionType') {
        const currentPort = prev.sshCredentials.port
        // Only auto-update if port is at a default value
        if (currentPort === 22 || currentPort === 23) {
          updates.port = value === 'telnet' ? 23 : 22
        }
      }
      
      return {
        ...prev,
        sshCredentials: {
          ...prev.sshCredentials,
          ...updates
        }
      }
    })
    setSuccess(null)
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Build payload - only include SSH credentials if all fields are filled
      const payload = {
        vncHost: config.vncHost,
        vncPort: parseInt(config.vncPort, 10) || 5900,
        enabled: config.enabled,
        requiresActivation: config.requiresActivation,
        activationScript: config.activationScript || null,
        fileTransferEnabled: config.fileTransferEnabled,
        vncAgentId: config.vncAgentId || null
      }

      // Only include password if user entered one
      if (config.vncPassword) {
        payload.vncPassword = config.vncPassword
      }

      // Only include SSH credentials if activation is required and all fields are filled
      if (config.requiresActivation) {
        const ssh = config.sshCredentials
        if (ssh.host && ssh.username && ssh.password) {
          payload.sshCredentials = {
            host: ssh.host,
            port: parseInt(ssh.port, 10) || (ssh.connectionType === 'telnet' ? 23 : 22),
            username: ssh.username,
            password: ssh.password,
            connectionType: ssh.connectionType || 'ssh'
          }
        }
      }

      await vncAPI.upsertConfig(id, payload)
      setHasExistingConfig(true)
      setSuccess('VNC configuration saved successfully')
      
      // Clear password fields after save
      setConfig(prev => ({
        ...prev,
        vncPassword: '',
        hasVncPassword: !!payload.vncPassword || prev.hasVncPassword,
        sshCredentials: {
          ...prev.sshCredentials,
          password: ''
        },
        hasSshCredentials: !!payload.sshCredentials || prev.hasSshCredentials
      }))
    } catch (err) {
      console.error('Error saving VNC config:', err)
      setError(err.message || 'Failed to save VNC configuration')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete the VNC configuration for this modality?')) {
      return
    }

    try {
      setDeleting(true)
      setError(null)
      await vncAPI.deleteConfig(id)
      setHasExistingConfig(false)
      setConfig({
        vncHost: '',
        vncPort: 5900,
        vncPassword: '',
        enabled: true,
        requiresActivation: false,
        activationScript: '',
        fileTransferEnabled: false,
        vncAgentId: null,
        sshCredentials: {
          host: '',
          port: 22,
          username: '',
          password: '',
          connectionType: 'ssh'
        }
      })
      setSuccess('VNC configuration deleted')
    } catch (err) {
      console.error('Error deleting VNC config:', err)
      setError(err.message || 'Failed to delete VNC configuration')
    } finally {
      setDeleting(false)
    }
  }

  if (!id) {
    return (
      <Box p={3}>
        <Alert severity="warning">No modality selected</Alert>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    )
  }

  const isValid = config.vncHost && config.vncPort

  return (
    <Box className={classes.form}>
      <SectionTitle label="VNC Remote Access Configuration" />

      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" style={{ marginBottom: 16 }}>
          {success}
        </Alert>
      )}

      <FormControlLabel
        control={
          <Switch
            checked={config.enabled}
            onChange={handleChange('enabled')}
            color="primary"
          />
        }
        label="Enable VNC for this modality"
        className={classes.field}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={8}>
          <TextField
            label="VNC Host"
            value={config.vncHost}
            onChange={handleChange('vncHost')}
            fullWidth
            variant="filled"
            size="small"
            className={classes.field}
            placeholder="e.g., 100.64.0.10"
            helperText="Tailscale IP address of the modality"
            required
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label="VNC Port"
            type="number"
            value={config.vncPort}
            onChange={handleChange('vncPort')}
            fullWidth
            variant="filled"
            size="small"
            className={classes.field}
            inputProps={{ min: 1, max: 65535 }}
          />
        </Grid>
      </Grid>

      <TextField
        label="VNC Password"
        type={showPassword ? 'text' : 'password'}
        value={config.vncPassword}
        onChange={handleChange('vncPassword')}
        fullWidth
        variant="filled"
        size="small"
        className={classes.field}
        helperText={config.hasVncPassword 
          ? "Password is set. Leave blank to keep existing, or enter new value to change."
          : "Optional. Used for file transfer authentication."
        }
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                size="small"
              >
                {showPassword ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </InputAdornment>
          )
        }}
      />

      <FormControlLabel
        control={
          <Switch
            checked={config.fileTransferEnabled}
            onChange={handleChange('fileTransferEnabled')}
            color="primary"
          />
        }
        label="Enable file transfer (TightVNC)"
        className={classes.field}
      />

      <Divider className={classes.divider} />

      <SectionTitle label="VNC Activation (Optional)" />
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Some modalities require SSH activation before VNC connection (e.g., GE systems with gemsvnc).
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={config.requiresActivation}
            onChange={handleChange('requiresActivation')}
            color="primary"
          />
        }
        label="Requires activation before connecting"
        className={classes.field}
      />

      {config.requiresActivation && (
        <>
          <TextField
            label="Activation Script"
            value={config.activationScript}
            onChange={handleChange('activationScript')}
            fullWidth
            variant="filled"
            size="small"
            multiline
            rows={2}
            className={classes.field}
            placeholder="/usr/g/insite/RCOC/arch/linux-suseE10/bin/gemsvnc -wait4client 0"
            helperText="Command to run via SSH to activate VNC server"
          />

          <FormControl 
            fullWidth 
            variant="filled" 
            size="small" 
            className={classes.field}
          >
            <InputLabel>VNC Activation Agent</InputLabel>
            <Select
              value={config.vncAgentId || ''}
              onChange={(e) => setConfig(prev => ({ 
                ...prev, 
                vncAgentId: e.target.value || null 
              }))}
              disabled={loadingAgents}
            >
              <MenuItem value="">
                <em>Use default (data collection agent)</em>
              </MenuItem>
              {agents.map(agent => (
                <MenuItem key={agent.id} value={agent.id}>
                  {agent.description} (ID: {agent.id})
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Select which agent should activate VNC. Use this when the data collection agent cannot reach the modality via SSH.
            </FormHelperText>
          </FormControl>

          <Typography variant="subtitle2" className={classes.section}>
            Connection Credentials for Activation
          </Typography>

          <FormControl 
            fullWidth 
            variant="filled" 
            size="small" 
            className={classes.field}
          >
            <InputLabel>Connection Type</InputLabel>
            <Select
              value={config.sshCredentials.connectionType || 'ssh'}
              onChange={handleSshChange('connectionType')}
            >
              <MenuItem value="ssh">SSH</MenuItem>
              <MenuItem value="telnet">Telnet</MenuItem>
            </Select>
            <FormHelperText>
              SSH is standard. Use Telnet for modalities that don't have SSH enabled.
            </FormHelperText>
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={8}>
              <TextField
                label="Host"
                value={config.sshCredentials.host}
                onChange={handleSshChange('host')}
                fullWidth
                variant="filled"
                size="small"
                className={classes.field}
                placeholder="Same as VNC host if on same machine"
                helperText={config.hasSshCredentials ? "Credentials are set. Enter values to update." : ""}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Port"
                type="number"
                value={config.sshCredentials.port}
                onChange={handleSshChange('port')}
                fullWidth
                variant="filled"
                size="small"
                className={classes.field}
                inputProps={{ min: 1, max: 65535 }}
                helperText={config.sshCredentials.connectionType === 'telnet' ? 'Default: 23' : 'Default: 22'}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                value={config.sshCredentials.username}
                onChange={handleSshChange('username')}
                fullWidth
                variant="filled"
                size="small"
                className={classes.field}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                type={showSshPassword ? 'text' : 'password'}
                value={config.sshCredentials.password}
                onChange={handleSshChange('password')}
                fullWidth
                variant="filled"
                size="small"
                className={classes.field}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowSshPassword(!showSshPassword)}
                        edge="end"
                        size="small"
                      >
                        {showSshPassword ? <Visibility /> : <VisibilityOff />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </>
      )}

      <Box className={classes.actions}>
        <div>
          {hasExistingConfig && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleDelete}
              disabled={deleting || saving}
              startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              Delete Configuration
            </Button>
          )}
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving || !isValid}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          Save VNC Configuration
        </Button>
      </Box>
    </Box>
  )
}

export default VncConfigPanel

